import { cConst, cRange, defaultLayer, gradient, vConst, vRange } from '../defaults.js';
import { curvePreset } from '../math/curve.js';
/**
 * Reference effect #4: ink/paint splat, Splatoon-style (plan §"Reference
 * effects"): flipbook burst + velocity-stretched droplets + a stain decal quad
 * whose blobby edge comes from a node-graph material (worley × mask → toon).
 */
export const inkSplatEffect = {
    version: 1,
    meta: { name: 'Ink Splat', duration: 1.6, loop: false, simulationSpace: 'local' },
    layers: [
        defaultLayer({
            id: 'ink_burst',
            name: 'splat burst',
            delay: 0,
            duration: 0.06,
            maxParticles: 6,
            emitter: {
                mode: { type: 'burst', count: 2 },
                shape: { type: 'sphere', radius: 0.12 },
                initial: {
                    lifetime: vRange(0.4, 0.55),
                    speed: vRange(0.2, 0.6),
                    spread: vConst(30),
                    size: vRange(1.1, 1.5),
                    rotation: vRange(0, 360),
                    angularVelocity: vRange(-90, 90),
                    color: cConst([0.55, 0.15, 0.85, 1]),
                },
            },
            renderer: { type: 'flipbook', rows: 4, cols: 4, playback: { mode: 'lifetime' } },
            modules: [
                { type: 'sizeOverLife', enabled: true, curve: curvePreset('easeOutBack') },
                {
                    type: 'colorOverLife',
                    enabled: true,
                    gradient: gradient('smooth', [0, [1, 1, 1, 1]], [0.7, [1, 0.9, 1, 1]], [1, [1, 0.8, 1, 0]]),
                },
            ],
            material: { blend: 'alpha', sprite: { type: 'builtin', id: 'smokeSheet' } },
        }),
        defaultLayer({
            id: 'ink_droplets',
            name: 'droplets',
            delay: 0.02,
            duration: 0.06,
            maxParticles: 32,
            emitter: {
                mode: { type: 'burst', count: 16 },
                shape: { type: 'cone', angle: 55, radius: 0.15 },
                initial: {
                    lifetime: vRange(0.35, 0.7),
                    speed: vRange(2.5, 5.5),
                    spread: vConst(12),
                    size: vRange(0.08, 0.2),
                    rotation: vConst(0),
                    color: cRange([0.6, 0.2, 0.9, 1], [0.4, 0.1, 0.7, 1]),
                },
            },
            // droplets elongate with velocity — the anime speed-droplet look
            renderer: { type: 'stretchedBillboard', stretch: vRange(0.25, 0.5) },
            modules: [
                { type: 'gravity', enabled: true, strength: 14 },
                {
                    type: 'colorOverLife',
                    enabled: true,
                    gradient: gradient('smooth', [0, [1, 1, 1, 1]], [0.85, [1, 1, 1, 1]], [1, [1, 1, 1, 0]]),
                },
            ],
            material: { blend: 'alpha', sprite: { type: 'builtin', id: 'softDot' } },
        }),
        defaultLayer({
            id: 'ink_stain',
            name: 'stain decal',
            delay: 0.08,
            duration: 0.05,
            maxParticles: 2,
            emitter: {
                mode: { type: 'burst', count: 1 },
                shape: { type: 'point' },
                initial: {
                    lifetime: vConst(1.4),
                    speed: vConst(0),
                    spread: vConst(0),
                    size: vConst(1.7),
                    rotation: vRange(0, 360),
                    color: cConst([0.5, 0.13, 0.8, 0.95]),
                },
            },
            renderer: { type: 'procMesh', shape: { kind: 'quad', orientation: 'ground' } },
            modules: [
                { type: 'sizeOverLife', enabled: true, curve: curvePreset('easeOutBack') },
                {
                    type: 'colorOverLife',
                    enabled: true,
                    gradient: gradient('smooth', [0, [1, 1, 1, 1]], [0.7, [1, 1, 1, 1]], [1, [1, 1, 1, 0]]),
                },
            ],
            material: {
                blend: 'alpha',
                sprite: { type: 'builtin', id: 'hardDot' },
                // blobby stain silhouette: worley noise × radial mask → 2-step toon band
                graph: {
                    nodes: [
                        { id: 'uv0', type: 'uv', x: 0, y: 160 },
                        { id: 'noise', type: 'textureSample', x: 170, y: 100, params: { sprite: { type: 'builtin', id: 'noise' } } },
                        { id: 'mask', type: 'textureSample', x: 170, y: 260, params: { sprite: { type: 'builtin', id: 'softDot' } } },
                        { id: 'mul', type: 'multiply', x: 360, y: 170 },
                        { id: 'toon', type: 'toonRamp', x: 530, y: 170, params: { steps: 2 } },
                        { id: 'out', type: 'output', x: 710, y: 170 },
                    ],
                    edges: [
                        { id: 'e1', from: 'uv0', fromPort: 'out', to: 'noise', toPort: 'uv' },
                        { id: 'e2', from: 'uv0', fromPort: 'out', to: 'mask', toPort: 'uv' },
                        { id: 'e3', from: 'noise', fromPort: 'out', to: 'mul', toPort: 'a' },
                        { id: 'e4', from: 'mask', fromPort: 'out', to: 'mul', toPort: 'b' },
                        { id: 'e5', from: 'mul', fromPort: 'out', to: 'toon', toPort: 'in' },
                        { id: 'e6', from: 'toon', fromPort: 'out', to: 'out', toPort: 'color' },
                        { id: 'e7', from: 'mul', fromPort: 'out', to: 'out', toPort: 'alpha' },
                    ],
                },
            },
        }),
    ],
};
